import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import './../Styles/Login.css';
import './../Styles/LandingPage.css';
import logoLight from './../Images/tsqs-hm-ft-logo1.gif';
import logoDark from './../Images/tsqs-hm-ft-logo.gif';
import { useToast } from './ToastContext';
import { useTheme } from './ThemeContext';
import { FiSun, FiMoon, FiEye, FiEyeOff } from 'react-icons/fi';

const Login = () => {
  const { theme, toggleTheme } = useTheme();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const showToast = useToast();
  
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  
  const handleForgotPassword = (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      showToast('Email is required', 'error');
      return;
    }
    axios.post(`${apiUrl}/api/forgot-password`, { email: forgotEmail })
      .then(response => {
        showToast(response.data.message || 'Password sent to your email successfully.', 'success');
        setShowForgotModal(false);
        setForgotEmail('');
      })
      .catch(error => {
        if (error.response && error.response.status === 404) {
          showToast('Only registered users get password mail', 'error');
        } else {
          showToast('Failed to send email. Please try again later.', 'error');
        }
      });
  };
  
  const handleLogin = (e) => {
    e.preventDefault();

    if (!username && !password) {
      showToast('Email and Password are required', 'error');
      return;
    } else if (!username) {
      showToast('Email is required', 'error');
      return;
    } else if (!password) {
      showToast('Password is required', 'error');
      return;
    }

    axios.post(`${apiUrl}/login`, { username, password })
      .then(response => {
        const { role, employeeId, isMainManager } = response.data;
        showToast('Login successful!', 'success');
        localStorage.setItem('employeeId', employeeId);
        localStorage.setItem('userRole', role);
        localStorage.setItem('isMainManager', isMainManager);

        // Route everyone to the landing page first
        navigate('/employee-projects', { replace: true });
      })
      .catch(error => {
        console.error(error);
        if (error.response) {
          showToast(`Login failed: ${error.response.data}`, 'error');
        } else if (error.request) {
          showToast('Login failed: No response from server', 'error');
        } else {
          showToast(`Login failed: ${error.message}`, 'error');
        }
      });
  };

  return (
    <div className="login-page">
      <header className="lp-header" style={{ width: '100%', boxSizing: 'border-box' }}>
        <div className="lp-logo-container">
          <>
            <img src={logoLight} alt="TSQS Logo" className="lp-logo logo-light" />
            <img src={logoDark} alt="TSQS Logo" className="lp-logo logo-dark" />
          </>
        </div>
        <div className="lp-title">
          <h1 className="lp-wordmark" aria-label="Tconnect">
            <span className="lp-wordmark-t">T</span>
            <span className="lp-wordmark-connect">connect</span>
          </h1>
        </div>
        <div className="lp-nav-controls">
          {/* THEME TOGGLE - Uncomment to re-enable dark mode toggle
          <button
            className="lp-theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          */}
          <button className="lp-btn lp-btn-outline" style={{ padding: '0.4rem 1rem', minWidth: 'auto', fontSize: '0.9rem' }} onClick={() => navigate('/')}>
            Home
          </button>
        </div>
      </header>
      <div className="login-container">
        <div className="login-card">
          <h2>LOGIN</h2>
          <form onSubmit={handleLogin} className="login-form">
            <div className="pms-form-group">
              <label className="pms-label">Email: <span className="required-asterisk">*</span></label>
              <input
                className="pms-input"
                type="email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="pms-form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label className="pms-label" style={{ marginBottom: 0 }}>Password: <span className="required-asterisk">*</span></label>
                <span onClick={() => setShowForgotModal(true)} style={{cursor: 'pointer', color: 'var(--brand)', fontSize: '13px', fontWeight: 'bold'}}>Forgot Password?</span>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  className="pms-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ width: '100%', paddingRight: '40px' }}
                />
                <span 
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </span>
              </div>
            </div>
            <button type="submit" className="pms-btn pms-btn-primary login-btn-full">Login</button>
          </form>
          <p className="login-footer-text">
            Don't have an account? <Link to="/register" className="register-link">Register here</Link>
          </p>
        </div>
      </div>
      
      {showForgotModal && (
        <div className="modal-overlay" style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000}}>
          <div className="modal-content" style={{backgroundColor: 'var(--bg-card)', padding: '30px', borderRadius: '12px', width: '350px', boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border)'}}>
            <h3 style={{marginTop: 0, color: 'var(--text-primary)'}}>Forgot Password</h3>
            <p style={{fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '20px'}}>Enter your registered email address to receive your password.</p>
            <form onSubmit={handleForgotPassword}>
              <div className="pms-form-group">
                <label className="pms-label">Email: <span className="required-asterisk">*</span></label>
                <input
                  className="pms-input"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  style={{width: '100%', boxSizing: 'border-box'}}
                  placeholder="name@example.com"
                />
              </div>
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px'}}>
                <button type="button" className="pms-btn" style={{backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)', border: '1px solid var(--border)'}} onClick={() => setShowForgotModal(false)}>Cancel</button>
                <button type="submit" className="pms-btn pms-btn-primary">Send Email</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
